import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell, { storedSession } from '../components/AppShell';
import ChainStepper, { when } from '../components/workflows/ChainStepper';
import { OutOfOfficeDialog } from '../components/workflows/OutOfOffice';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Skeleton, ToastProvider, useToast } from '../components/ui/Kit';

// /approvals (approved mockup Approvals): jobs waiting for your decision, oldest first. Mine · Delegated to me ·
// Decided (· All, company admins: read-only + reassign). Bulk approve within one step. The decision panel shows the
// chain, lets an APPROVE PAY step edit the range, and needs a comment to send back.
const TONE = { green: 'bg-[#DCFCE7] text-[#166534]', amber: 'bg-[#FEF3C7] text-[#92400E]', red: 'bg-[#FEE2E2] text-[#991B1B]' };
const pill = 'inline-flex items-center gap-1 px-[9px] py-[3px] rounded-full text-[11px] font-semibold whitespace-nowrap';

function sub(it) {
  return [it.req_id, it.requested_by ? `HM ${it.requested_by.split(' ')[0]}` : null,
    it.change && it.change.length ? (it.change.some((c) => c.field === 'compensation') ? 'pay change' : 'title change') :
      (it.openings ? `${it.openings} opening${it.openings > 1 ? 's' : ''}` : it.location)].filter(Boolean).join(' · ');
}

function whyText(it) {
  const c = (it.change || []).find((x) => x.field === 'compensation');
  if (c && it.why && it.why.reason === 'approver') return `Pay change: ${c.current || '—'} → ${c.proposed}`;
  return it.why ? it.why.text : '';
}

function remindedToday(iso) {
  if (!iso) return false;
  const d = new Date(`${iso}Z`);
  return d.toDateString() === new Date().toDateString();
}

function Panel({ it, me, onDone, tab }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [chain, setChain] = useState(null);
  const [comment, setComment] = useState('');
  const [pay, setPay] = useState({ min: '', max: '' });
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setComment(''); setError(''); setTo('');
    setPay(it.pay_edit ? { min: it.pay_edit.base_min ?? '', max: it.pay_edit.base_max ?? '' } : { min: '', max: '' });
    setChain(null);
    api(`/api/jobs/${it.requirement_id}/chain`).then(setChain).catch(() => setChain({ steps: [] }));
  }, [it.id, it.requirement_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (it.can_reassign) api('/api/org/setup').then((s) => setPeople(s.people || [])).catch(() => setPeople([]));
  }, [it.can_reassign]);
  const decide = async (decision) => {
    setError(''); setBusy(true);
    const body = { decision, comment };
    if (decision === 'approve' && it.pay_edit && (String(pay.min) !== String(it.pay_edit.base_min ?? '') || String(pay.max) !== String(it.pay_edit.base_max ?? ''))) {
      body.compensation = { currency: it.pay_edit.currency, base_min: Number(String(pay.min).replace(/[^0-9.]/g, '')), base_max: Number(String(pay.max).replace(/[^0-9.]/g, '')) };
    }
    try { await api(`/api/approvals/${it.id}/decide`, { method: 'POST', body }); toast.show(decision === 'approve' ? 'Approved' : 'Sent back to the hiring manager'); onDone(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const reassign = async () => {
    setError(''); setBusy(true);
    try { await api(`/api/approvals/${it.id}/reassign`, { method: 'POST', body: { user_id: Number(to), reason: comment } }); toast.show('Approval reassigned'); onDone(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const meta = [it.department, it.location, it.work_mode, it.employment_type].filter(Boolean).join(' · ');
  return (
    <aside className={card + ' rounded-[16px] p-5 flex flex-col gap-4 lg:w-[400px] shrink-0'} aria-label="Decision panel" data-testid="decision-panel">
      <div>
        <p className="text-xs font-bold uppercase tracking-[.05em] text-[#64748B]">{it.req_id}</p>
        <p className="text-lg font-extrabold mt-0.5">{it.job_title}</p>
        {meta && <p className="text-[13px] text-[#64748B]">{meta}</p>}
        <button type="button" className="text-[13px] font-semibold text-[#0066CC] mt-1.5" onClick={() => navigate(`/jobs/${it.requirement_id}`)}>Open job page →</button>
      </div>
      {!chain ? <Skeleton className="h-28 w-full" /> : (chain.steps.length || tab !== 'decided') ? <ChainStepper chain={chain} me={me} /> : null}
      {tab === 'decided' && (
        <p className="text-sm text-[#334155]" data-testid="decided-note">You {it.decision === 'approved' ? 'approved' : 'sent back'} this{it.decided_at ? ` · ${when(it.decided_at)}` : ''}{it.on_behalf_of ? ` on behalf of ${it.on_behalf_of}` : ''}{it.comment ? ` — “${it.comment}”` : ''}</p>
      )}
      {it.can_decide && (
        <>
          {it.pay_edit && (
            <div>
              <div className="grid grid-cols-2 gap-2.5">
                <label className="block"><span className={labelCls}>Pay min ({it.pay_edit.currency})</span>
                  <input className={input} inputMode="numeric" value={pay.min} onChange={(e) => setPay({ ...pay, min: e.target.value })} /></label>
                <label className="block"><span className={labelCls}>Pay max ({it.pay_edit.currency})</span>
                  <input className={input} inputMode="numeric" value={pay.max} onChange={(e) => setPay({ ...pay, max: e.target.value })} /></label>
              </div>
              <p className="text-xs text-[#64748B] mt-1.5">You can adjust the range. The hiring manager is told old → new.</p>
            </div>
          )}
          <label className="block"><span className={labelCls}>Comment (required to send back)</span>
            <textarea rows={3} className={input + ' resize-none'} placeholder="Add context for the hiring manager…" value={comment} onChange={(e) => setComment(e.target.value)} /></label>
          <InlineError>{error}</InlineError>
          <div className="flex gap-2.5">
            <button type="button" className="flex-1 inline-flex justify-center items-center min-h-[40px] rounded-[10px] bg-[#15803D] text-white text-sm font-semibold disabled:opacity-50" disabled={busy} onClick={() => decide('approve')}>Approve</button>
            <button type="button" className={btnSecondary + ' flex-1 justify-center'} disabled={busy} onClick={() => decide('reject')}>Send back</button>
          </div>
        </>
      )}
      {it.can_reassign && (
        <>
          <p className="text-sm text-[#334155]">Waiting on {(it.approvers || []).join(', ') || 'company admins'}.</p>
          <label className="block"><span className={labelCls}>Reassign to</span>
            <select className={input} value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Choose a person…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select></label>
          <label className="block"><span className={labelCls}>Reason (on the job's timeline)</span>
            <input className={input} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Approver on leave" /></label>
          <InlineError>{error}</InlineError>
          <button className={btnPrimary} disabled={busy || !to} onClick={reassign}>Reassign</button>
        </>
      )}
    </aside>
  );
}

function Inner() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = storedSession();
  const admin = user.role === 'company_super_admin';
  const initial = new URLSearchParams(window.location.search).get('tab');
  const [tab, setTab] = useState(['mine', 'delegated', 'decided', 'all'].includes(initial) ? initial : 'mine');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(null);
  const [picked, setPicked] = useState([]);
  const [ooo, setOoo] = useState(false);
  const load = useCallback(async () => {
    setError('');
    try {
      const d = await api(`/api/approvals?tab=${tab}`);
      setData(d);
      setSel((cur) => (cur && d.items.some((x) => x.id === cur) ? cur : (d.items[0] ? d.items[0].id : null)));
      setPicked((p) => p.filter((id) => d.items.some((x) => x.id === id)));
    } catch (e) { setError(e.message); setData({ items: [], counts: {} }); }
  }, [tab]);
  useEffect(() => { load(); window.history.replaceState(null, '', `/approvals${tab === 'mine' ? '' : `?tab=${tab}`}`); }, [load, tab]);
  const items = (data && data.items) || [];
  const counts = (data && data.counts) || {};
  const current = items.find((x) => x.id === sel);
  const pickedItems = items.filter((x) => picked.includes(x.id));
  const sameStep = pickedItems.length > 0 && new Set(pickedItems.map((x) => x.bulk_key)).size === 1;
  const canPick = tab === 'mine' || tab === 'delegated';
  const bulk = async () => {
    try {
      const r = await api('/api/approvals/bulk', { method: 'POST', body: { ids: picked } });
      toast.show(`Approved ${r.approved} of ${picked.length}`); setPicked([]); load();
    } catch (e) { toast.show(e.message, 'error'); }
  };
  const tabs = [['mine', 'Mine', counts.mine, 'bg-[#FEE2E2] text-[#991B1B]'], ['delegated', 'Delegated to me', counts.delegated, 'bg-[#E2E8F0] text-[#334155]'],
    ['decided', 'Decided'], ...(admin ? [['all', 'All']] : [])];
  return (
    <AppShell active="approvals">
      <div className="space-y-[18px]" data-testid="approvals-page">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]"><h1 className="text-[26px] font-extrabold m-0">Approvals</h1>
            <p className="text-sm text-[#64748B] mt-1">Jobs waiting for your decision. Oldest first.</p></div>
          <button className={btnSecondary} onClick={() => setOoo(true)}>Out of office…</button>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div role="tablist" aria-label="Approval views" className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl max-w-full overflow-x-auto">
            {tabs.map(([k, l, n, tone]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setSel(null); setPicked([]); }}
                className={`inline-flex items-center gap-1.5 min-h-[36px] px-3.5 rounded-[9px] text-[13px] font-semibold whitespace-nowrap ${tab === k ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}>
                {l}{n > 0 && <span className={`${pill} ${tone}`}>{n}</span>}</button>
            ))}
          </div>
          <div className="flex-1" />
          {canPick && <button className={btnSecondary + ' !py-1.5'} disabled={!sameStep} onClick={bulk}
            title={pickedItems.length && !sameStep ? 'Bulk approve works within one step' : undefined}>
            Approve {pickedItems.length || ''} selected</button>}
        </div>
        <InlineError>{error}</InlineError>
        {data === null ? <Skeleton className="h-48 w-full" /> : !items.length ? (
          <div className={card + ' rounded-[16px]'}><EmptyState title={tab === 'decided' ? 'Nothing decided in the last 90 days' : 'Nothing waiting for you'}
            hint={tab === 'mine' ? 'New approvals show up here and in your bell.' : undefined} action={<button className={btnSecondary} onClick={() => navigate('/jobs')}>Go to jobs</button>} /></div>
        ) : (
          <div className="flex gap-[18px] flex-col lg:flex-row items-start">
            <section className={card + ' rounded-[16px] overflow-hidden flex-1 min-w-0 w-full'}>
              <div className="overflow-x-auto hidden md:block">
                <table className="w-full border-collapse text-[13px] min-w-[640px]">
                  <thead className="text-[11px] text-[#64748B] font-bold uppercase tracking-[.05em] bg-[#FAFBFD]">
                    <tr>
                      <th className="w-8 px-3 py-3 border-b border-[#E6E9F0]">{canPick && <input type="checkbox" aria-label="Select all" checked={picked.length > 0 && picked.length === items.length}
                        onChange={(e) => setPicked(e.target.checked ? items.map((x) => x.id) : [])} />}</th>
                      {['Job', 'Step · why you', 'Waiting', 'Pay'].map((h) => <th key={h} scope="col" className="text-left px-3 py-3 border-b border-[#E6E9F0]">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} onClick={() => setSel(it.id)} className={`cursor-pointer ${sel === it.id ? 'bg-[#F5F9FF] [&>td:first-child]:shadow-[inset_3px_0_0_#0066CC]' : 'hover:bg-[#FAFBFD]'}`} data-testid="approval-row">
                        <td className="px-3 py-3 border-b border-[#EEF1F6] align-top" onClick={(e) => e.stopPropagation()}>{canPick &&
                          <input type="checkbox" aria-label={`Select ${it.job_title}`} checked={picked.includes(it.id)}
                            onChange={(e) => setPicked(e.target.checked ? [...picked, it.id] : picked.filter((x) => x !== it.id))} />}</td>
                        <td className="px-3 py-3 border-b border-[#EEF1F6] align-top"><div className="font-bold">{it.job_title}</div><div className="text-xs text-[#64748B]">{sub(it)}</div></td>
                        <td className="px-3 py-3 border-b border-[#EEF1F6] align-top"><b>{it.step_label}</b><div><span className="inline-block mt-1 text-[11.5px] text-[#475569] bg-[#F1F5F9] rounded-md px-[7px] py-0.5">{whyText(it)}</span></div></td>
                        <td className="px-3 py-3 border-b border-[#EEF1F6] align-top"><span className={`${pill} ${TONE[it.tone] || TONE.green}`}>{it.waiting_label}</span>
                          {tab !== 'decided' && remindedToday(it.reminded_at) && <div className="text-[11px] text-[#64748B] mt-1">Reminded today</div>}
                          {tab === 'decided' && <div className="text-[11px] text-[#64748B] mt-1">{it.decision === 'approved' ? 'Approved' : 'Sent back'}</div>}</td>
                        <td className="px-3 py-3 border-b border-[#EEF1F6] align-top">{it.pay || '—'}{it.pay_note && <div className="text-[11px] text-[#64748B]">{it.pay_note}</div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="md:hidden divide-y divide-[#EEF1F6]">
                {items.map((it) => (
                  <li key={it.id} className={`p-4 ${sel === it.id ? 'bg-[#F5F9FF]' : ''}`} data-testid="approval-card" onClick={() => setSel(it.id)}>
                    <div className="flex items-start gap-3">
                      {canPick && <input type="checkbox" aria-label={`Select ${it.job_title}`} checked={picked.includes(it.id)} onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setPicked(e.target.checked ? [...picked, it.id] : picked.filter((x) => x !== it.id))} />}
                      <div className="flex-1 min-w-0"><p className="font-bold">{it.job_title}</p><p className="text-xs text-[#64748B]">{sub(it)}</p>
                        <p className="text-[13px] mt-1"><b>{it.step_label}</b> · {it.pay || '—'}</p><p className="text-[11.5px] text-[#475569]">{whyText(it)}</p></div>
                      <span className={`${pill} ${TONE[it.tone] || TONE.green}`}>{it.waiting_label}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            {current && <Panel it={current} me={user.full_name} tab={tab} onDone={load} />}
          </div>
        )}
      </div>
      <OutOfOfficeDialog open={ooo} onClose={() => setOoo(false)} />
    </AppShell>
  );
}

export default function ApprovalsPage() {
  const navigate = useNavigate();
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <ToastProvider><Inner /></ToastProvider>;
}
